import styled from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { MULTIPLE_CARD_WIDTH, SINGLE_CARD_HEIGHT, SINGLE_CARD_WIDTH } from "@dineout-payment-success-ui/constants";
import { MULTIPLE_CARD_HEIGHT } from "@dineout-payment-success-ui/constants";

interface ICardBody {
    isMultipleCard?: boolean;
    $backgroundImage: string;
}

export const SavingsCardWrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    paddingTop: theme.spacing.xSmall,
}));

export const CardsWrapper = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing.xSmall,
    marginBottom: theme.spacing.small,
    marginTop: theme.spacing.small,
    "@media (max-width: 374px) and (min-width: 340px)": {
        marginBottom: "-6px",
        marginTop: "-10px",
    },
    "@media (max-width: 385px) and (min-width: 375px)": {
        marginBottom: "-6px",
        marginTop: `calc(${theme.spacing.xSmall} * -1)`,
    },
    "@media (max-width: 400px) and (min-width: 385px)": {
        marginBottom: theme.spacing.xxSmall,
        marginTop: theme.spacing.xxxSmall,
    },
}));

export const CardBody = styled.div<ICardBody>(({ isMultipleCard, $backgroundImage }) => ({
    display: "flex",
    position: "relative",
    flexDirection: "column",
    height: isMultipleCard ? MULTIPLE_CARD_HEIGHT : SINGLE_CARD_HEIGHT,
    width: isMultipleCard ? MULTIPLE_CARD_WIDTH : SINGLE_CARD_WIDTH,
    backgroundImage: `url(${$backgroundImage})`,
    backgroundSize: "100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    zIndex: "2",
    "@media (max-width: 510px) and (min-width: 446px)": {
        height: isMultipleCard ? "220px" : "160px",
    },
    "@media (max-width: 599px) and (min-width: 510px)": {
        height: isMultipleCard ? "250px" : "190px",
    },
}));

export const CartHeader = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "48px",
    padding: theme.spacing.xxSmall,
    background: "linear-gradient(180deg, rgba(233, 233, 233, 0.00) 41.03%, #F5F5F5 100%)",
    borderTopLeftRadius: theme.spacing.medium,
    borderTopRightRadius: theme.spacing.medium,
}));

export const CartHeaderTitle = styled(Text)(({ theme }) => ({
    padding: theme.spacing.small,
    letterSpacing: "1.5px",
}));

export const CartDetailsItem = styled.div(({ theme }) => ({
    position: "absolute",
    top: "28%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing.small,
    gap: theme.spacing.small,
    "@media (max-width: 499px) and (min-width: 340px)": {
        paddingTop: "18px",
        paddingRight: theme.spacing.small,
        paddingLeft: "14px",
        gap: theme.spacing.xSmall,
    },
    "@media (max-width: 499px) and (min-width: 400px)": {
        paddingRight: "15px",
    },
    "@media (max-width: 599px) and (min-width: 499px)": {
        paddingLeft: theme.spacing.large,
        paddingTop: theme.spacing.large,
    },
}));

export const AmountWrapper = styled(Text)(() => ({
    "@media (max-width: 425px) and (min-width: 400px)": {
        fontSize: "28px",
    },
}));

export const TextWrapper = styled(Text)(({ theme }) => ({
    img: {
        verticalAlign: `calc(${theme.spacing.xxxSmall} * -1)`,
    },
}));

export const CartItems = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    position: "relative",
    top: "35%",
    padding: theme.spacing.medium,

    "@media (max-width: 500px) and (min-width: 445px)": {
        top: "38%",
    },
    "@media (max-width: 599px) and (min-width: 500px)": {
        top: "42%",
    },
}));

export const VerticalDivider = styled.div(({ theme }) => ({
    width: "1px",
    height: "100%",
    backgroundColor: theme.colors[theme.colorMode].border_Tertiary,
    margin: `0 ${theme.spacing.medium}`,
}));

export const Nudge = styled.div(({ theme }) => ({
    backgroundColor: "#FFF0CD",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing.small,
    marginLeft: `calc(${theme.spacing.xxSmall} * -1)`,
    marginTop: theme.spacing.xxxSmall,
    padding: theme.spacing.xxSmall,
    "@media (max-width: 399px) and (min-width: 340px)": {
        marginTop: `calc(${theme.spacing.xxxSmall} * -1)`,
        padding: theme.spacing.xxxSmall,
    },
}));

export const NudgeNoSavings = styled.div(({ theme }) => ({
    width: "60%",
    backgroundColor: "#FFF0CD",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing.small,
    marginLeft: `calc(${theme.spacing.xxSmall} * -1)`,
    marginTop: theme.spacing.xxxSmall,
    padding: theme.spacing.xxSmall,
    "@media (max-width: 399px) and (min-width: 340px)": {
        marginTop: `calc(${theme.spacing.xxxSmall} * -1)`,
        padding: theme.spacing.xxxSmall,
    },
}));

export const AmountImageWrapper = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xxSmall,
}));

export const TextItem = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xxSmall,
}));

// ─── scenes-flow additions ────────────────────────────────────────────────────

const COIN_IMAGE_SIZE = "28px";
const SAVINGS_CARD_MIN_HEIGHT = "112px";

interface ISavingsCardRoot {
    $backgroundImage?: string;
}

const mixDivider = (a: string, b: string): string => `color-mix(in srgb, ${a} 22%, ${b})`;

export const SavingsStack = styled.div(({ theme }) => ({
    display: "flex",
    gap: theme.spacing.small,
    width: "100%",
}));

export const SavingsCardRoot = styled.div<ISavingsCardRoot>(({ theme, $backgroundImage }) => ({
    position: "relative",
    width: "100%",
    minHeight: SAVINGS_CARD_MIN_HEIGHT,
    boxSizing: "border-box",
    borderRadius: theme.cornerRadius.medium,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,

    overflow: "hidden",
    ...($backgroundImage && {
        backgroundImage: `url(${$backgroundImage})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
    }),
}));

export const SavingsHeaderStrip = styled.div<{ $isFullBanner?: boolean }>(({ theme, $isFullBanner }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: $isFullBanner ? undefined : "40px",
    padding: $isFullBanner ? 0 : `${theme.spacing.xxSmall} ${theme.spacing.small} ${theme.spacing.xxxSmall}`,
    background: $isFullBanner
        ? "none"
        : `linear-gradient(180deg, color-mix(in srgb, ${theme.colors[theme.colorMode].background_Primary} 16%, transparent) 0%, transparent 100%)`,
}));

export const SavingsHeaderTitle = styled(Text)(() => ({
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
}));

export const SavingsHeaderBanner = styled.div({
    width: "100%",
    lineHeight: 0,
    "& img": {
        width: "100%",
        height: "40px",
        display: "block",
        objectFit: "cover",
        objectPosition: "center",
    },
});

export const SavingsBodyRow = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    padding: `${theme.spacing.small} ${theme.spacing.small} ${theme.spacing.small}`,
}));

export const CoinImageWrap = styled.div(({ theme }) => ({
    width: COIN_IMAGE_SIZE,
    height: COIN_IMAGE_SIZE,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    filter: `drop-shadow(0 0 12px color-mix(in srgb, ${theme.colors[theme.colorMode].positive} 48%, transparent))`,
}));

export const AmountDisplay = styled(Text)({
    flexShrink: 0,
});

export const SavingsVerticalRule = styled.div(({ theme }) => ({
    width: "1px",
    alignSelf: "stretch",
    minHeight: "36px",
    backgroundColor: mixDivider(theme.colors[theme.colorMode].positive, theme.colors[theme.colorMode].border_Secondary),
    flexShrink: 0,
}));

export const DescriptionWrap = styled.div(({ theme }) => ({
    flex: 1,
    minWidth: 0,

    img: {
        verticalAlign: `calc(${theme.spacing.xxxSmall} * -1)`,
    },
}));

export const ScenesNudge = styled.div(({ theme }) => ({
    backgroundColor: theme.colors[theme.colorMode].warning_Sub,
    display: "flex",
    width: "fit-content",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing.small,
    marginTop: 4,
    padding: theme.spacing.xxSmall,
    "@media (max-width: 399px) and (min-width: 340px)": {
        padding: theme.spacing.xxxSmall,
    },
}));
